import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { connect } from "react-redux";
import get from "lodash.get";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import createIRIPDocument from "@/services/IRIP/createIRIPDocument";
import SRS from "../../../../services/StudentRecordService";
import { useTranslation } from "react-i18next";
import { getAssignments } from "@/services/assignment/getAssignment/getAssignment";
import {
  ENTITY_TYPE,
  ASSIGNMENT_STATUS,
  ASSIGNMENT_TYPE,
} from "@/services/assignment/constants";
import { getUserData } from "@/services/API";
import { getSchoolYearDates } from "../../../../services/IRIP/utils/schoolYearUtils";

// Report Util
import getReportData from "../../util/getReportData";

// Report Components
import ReadingRopeReport from "../../containers/ReadingRope";
import ProgressReport from "../../containers/Progress";

// Report State Creators
import progressCreators from "../../state/progress/creators";
import readingRopeCreators from "../../state/readingrope/creators";
import teacherCreators from "../../state/teacher/creators";
import rosterCreators from "../../state/roster/creators";

// SCSS
import "../ReadingRope/index.scss";
import "./IRIPDocumentGenerator.scss";
import "../../Reports.scss";
import "../ReportContainer/index.scss";

const IRIPDocumentGenerator = ({
  // State
  students,
  benchmarks,
  weeklyPlan,
  selectedStudent,
  selectedClass,
  selectedDistrict,
  selectedSchool,
  selectedMetric,
  selectedScale,
  selectedEquating,
  selectedLocale,
  selectedBenchmark,
  // Actions
  fetchProgressReport,
  fetchReadingRopeReport,
  updateTeacherSelection,
  updateRosterSelection,
}) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  // Reports
  const [progressReport, setProgressReport] = useState(null);
  const [readingRopeReport, setReadingRopeReport] = useState(null);

  // Report Promises
  const progressReportPromiseResolver = useRef(null);
  const readingRopeReportPromiseResolver = useRef(null);

  const initialAppStateRef = useRef({
    metric: null,
    scale: null,
    equating: null,
    locale: null,
    benchmark: null,
  });

  const initializeReportData = ({
    reportType,
    reportStudent,
    params,
    reportEquating,
    reportMetric,
    reportScale,
    reportBenchmark,
    selectedScreeningWindow,
    selectedStatePercentileCuts,
    reportLocale,
  }) => {
    getReportData(
      true,
      false,
      params,
      // "Props" Object (Can be reformatted with direct variable calls for modularity conceptualization)
      {
        params: {
          report: reportType,
        },
        // State
        students: students,
        selectedStudent: reportStudent,
        // Mock, grab from props 'benchmarks'
        benchmarks: reportMetric.benchmarks,
        selectedEquating: reportEquating,
        selectedMetric: reportMetric,
        selectedScale: reportScale,
        selectedBenchmark: reportBenchmark,
        selectedClass,
        selectedScreeningWindow,
        selectedStatePercentileCuts,
        selectedDistrict,
        selectedSchool,
        // Actions
        fetchProgressReport,
        fetchReadingRopeReport,
        selectedLocale: reportLocale,
      },
      false,
      () => {}
    );
  };

  const initializeProgressReport = (
    reportStudent,
    reportMetric,
    reportScale,
    reportEquating,
    reportLocale,
    reportBenchmark
  ) => {
    const domId = `progress-report-${reportStudent.id}`;

    const progressReport = (
      <div id={domId} className="progress-report-container">
        <ProgressReport
          initializeData={() =>
            initializeReportData({
              reportType: "progress",
              reportStudent,
              reportMetric,
              reportScale,
              reportBenchmark,
              selectedStatePercentileCuts: null,
              reportEquating,
              reportLocale,
            })
          }
          onDataLoaded={progressReportPromiseResolver.current}
        />
      </div>
    );
    setProgressReport(progressReport);

    return domId;
  };

  const initializeReadingRopeReport = (
    reportStudent,
    reportMetric,
    reportScale,
    reportEquating,
    reportLocale,
    reportBenchmark
  ) => {
    const domId = `reading-rope-${reportStudent.id}`;

    const readingRopeReport = (
      <div id={domId} className="reading-rope-report-container">
        <ReadingRopeReport
          initializeData={() =>
            initializeReportData({
              reportType: "readingrope",
              reportStudent,
              reportMetric,
              reportScale,
              reportBenchmark,
              selectedStatePercentileCuts: null,
              reportEquating,
            })
          }
          onDataLoaded={readingRopeReportPromiseResolver.current}
        />
      </div>
    );
    setReadingRopeReport(readingRopeReport);

    return domId;
  };

  const setupAppState = () => {
    // Store original state values before modifying them
    initialAppStateRef.current = {
      metric: selectedMetric,
      scale: selectedScale,
      equating: selectedEquating,
      locale: selectedLocale,
      benchmark: selectedBenchmark,
      student: selectedStudent,
    };

    const reportMetric = {
      label: "Amira Reading Mastery (ARM)",
      value: "ARM",
      scales: [
        {
          value: "ARM",
          label: "Amira Reading Mastery (ARM)",
        },
      ],
      benchmarks: [
        {
          value: "NationalNorms",
          grades: [0, 1, 2, 3, 4, 5, 6],
          seasons: ["Fall", "Winter", "Spring"],
          label: "National Norms",
        },
      ],
      description: "",
    };
    const reportScale = {
      label: "Amira Reading Mastery (ARM)",
      value: "ARM",
    };
    const reportEquating = true;
    const reportLocale = "EN-US";
    const reportBenchmark = {
      value: "NationalNorms_2",
      label: "National Norms - Grade 2",
      grade: 2,
    };

    // Update state for report generation
    // TODO: Tailor these for the desired report generation
    updateTeacherSelection(
      {
        type: "metric",
        data: reportMetric,
      },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      {
        type: "scale",
        data: reportScale,
      },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      {
        type: "equating",
        data: reportEquating,
      },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      {
        type: "locale",
        data: reportLocale,
      },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      {
        type: "benchmark",
        data: reportBenchmark,
      },
      selectedDistrict,
      students
    );

    return {
      reportMetric,
      reportScale,
      reportEquating,
      reportLocale,
      reportBenchmark,
    };
  };

  const restoreInitialAppState = () => {
    const { metric, scale, equating, locale, benchmark, student } =
      initialAppStateRef.current;

    // Restore original state values
    updateTeacherSelection(
      { type: "metric", data: originalSelectedMetric },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      { type: "scale", data: originalSelectedScale },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      { type: "equating", data: originalSelectedEquating },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      { type: "locale", data: originalSelectedLocale },
      selectedDistrict,
      students
    );
    updateTeacherSelection(
      { type: "benchmark", data: originalSelectedBenchmark },
      selectedDistrict,
      students
    );
    updateRosterSelection({
      type: "student",
      data: originalSelectedStudent,
    });
  };

  // Listen for IRIP generation events from other components (like the instruct widget)
  useEffect(() => {
    const handleGenerateEvent = (event) => {
      // Use students data from event if provided, otherwise fall back to props
      const eventStudents = event.detail?.students;
      const eventWeeklyPlan = event.detail?.weeklyPlan;
      
      if (eventStudents && eventStudents.length > 0) {
        console.log(`Using ${eventStudents.length} students from event data`);
        generateReportDocumentsWithData(eventStudents, eventWeeklyPlan);
      } else {
        console.log(`Using ${students.length} students from props`);
        generateReportDocuments();
      }
    };
    
    window.addEventListener('generateIRIPs', handleGenerateEvent);
    return () => window.removeEventListener('generateIRIPs', handleGenerateEvent);
  }, [generateReportDocuments, students]);

  const getStudentAssignments = async (student) => {
    try {
      const districtId = getUserData().districtId;

      // Get all assignments for the student
      const assignments = await getAssignments(
        student.id,
        ENTITY_TYPE.STUDENT,
        student.grade,
        selectedSchool.id,
        districtId,
        "CURRICULUM"  // Filter for CURRICULUM assignments only
      );


      // Filter for ASSIGNED status (CURRICULUM type already filtered by getAssignments)
      const assignedAssignments = assignments.filter(
        (assignment) => {
          const isAssigned = assignment.status === ASSIGNMENT_STATUS.ASSIGNED;
          
          return isAssigned;
        }
      );

      return assignedAssignments;
    } catch (error) {
      console.error("[Assignments] Error fetching student assignments:", error);
      return [];
    }
  };

  const generateReportDocuments = async () => {
    return generateReportDocumentsWithData(students, weeklyPlan);
  };

  const generateReportDocumentsWithData = async (studentsData, weeklyPlanData) => {
    console.log("WALOO: generateReportDocumentsWithData");
    console.log(`Students data: ${studentsData?.length || 0} students`);
    console.log(`Weekly plan data:`, weeklyPlanData);

    // Notify that generation has started
    window.dispatchEvent(new CustomEvent('iripProgress', { 
      detail: { isGenerating: true, progress: 0 } 
    }));

    // const {
    //   reportMetric,
    //   reportScale,
    //   reportEquating,
    //   reportLocale,
    //   reportBenchmark,
    // } = setupAppState();

    try {
      const metricsMap = await fetchStudentMetricsForStudents(studentsData);

      // Create a new zip file
      const zip = new JSZip();
      const reportFolder = zip.folder("irip_reports");

      // Generate reports for each student
      for (let i = 0; i < studentsData.length; i++) {
        const student = studentsData[i];

        const reportStudent = {
          ...student,
          value: student.id,
        };

        // If processing multiple students, update the selected student for each one
        updateRosterSelection({
          type: "student",
          data: reportStudent,
        });

        // Prepare student data for the report
        const studentData = {
          firstName: student.first_name || student.firstName || "",
          lastName: student.last_name || student.lastName || "",
          grade: student.grade || "",
          teacher: selectedClass?.teacher || "",
          school: selectedClass?.school || "",
          id: student.id || "",
          // Add any other required student data here
        };

        // Get metrics for this student from our action result
        const metrics = metricsMap[studentData.id] || {
          year: {
            activityCount: { total: 0 },
            incompleteActivityCount: { total: 0 },
            timeRead: { sum: 0 },
          },
          week: {
            activityCount: { total: 0 },
            incompleteActivityCount: { total: 0 },
            timeRead: { sum: 0 },
          },
        };

        const assignments = await getStudentAssignments(student);

        // Generate Reports
        // const progressReportPromise = new Promise((resolve) => {
        //   progressReportPromiseResolver.current = resolve;
        // });

        // const readingRopeReportPromise = new Promise((resolve) => {
        //   readingRopeReportPromiseResolver.current = resolve;
        // });

        // const progressReportId = initializeProgressReport(
        //   reportStudent,
        //   reportMetric,
        //   reportScale,
        //   reportEquating,
        //   reportLocale,
        //   reportBenchmark
        // );
        // const readingRopeReportId = initializeReadingRopeReport(
        //   reportStudent,
        //   reportMetric,
        //   reportScale,
        //   reportEquating,
        //   reportLocale,
        //   reportBenchmark
        // );

        // await Promise.all([progressReportPromise, readingRopeReportPromise]);

        // Make sure the reports are fully rendered before generating the document
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Generate the report as a blob
        try {
          const reportBlob = await createIRIPDocument(
            studentData,
            weeklyPlanData || weeklyPlan,
            metrics,
            assignments
            // progressReportId,
            // readingRopeReportId
          );

          if (reportBlob) {
            // Add the report to the zip file
            const fileName = `IRIP_${studentData.lastName}_${studentData.firstName}_${studentData.id}.docx`;
            reportFolder.file(fileName, reportBlob);
          }
        } catch (error) {
          console.error(
            `Error generating IRIP report for student: ${studentData.firstName} ${studentData.lastName} ${studentData.id}`,
            error
          );
        }

        // Update progress
        const progressPercent = Math.round(((i + 1) / studentsData.length) * 100);
        setProgress(progressPercent);
        
        // Notify other components of progress
        window.dispatchEvent(new CustomEvent('iripProgress', { 
          detail: { isGenerating: true, progress: progressPercent } 
        }));
      }

      // Generate the zip file and trigger download
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const classroomName = selectedClass?.name || "Classroom";
      const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
      saveAs(zipBlob, `IRIP_Reports_${classroomName}_${timestamp}.zip`);

      console.log("IRIP reports generation completed");
      
      // Notify that generation is complete
      window.dispatchEvent(new CustomEvent('iripProgress', { 
        detail: { isGenerating: false, progress: 100 } 
      }));
    } catch (error) {
      console.error("Error generating IRIP reports:", error);
      
      // Notify that generation failed
      window.dispatchEvent(new CustomEvent('iripProgress', { 
        detail: { isGenerating: false, progress: 0 } 
      }));
    } finally {
      // Reset all data to default state
      setIsGenerating(false);
      // setProgressReport(null);
      // setReadingRopeReport(null);
      progressReportPromiseResolver.current = null;
      readingRopeReportPromiseResolver.current = null;

      // restoreInitialAppState();
    }
  };

  const fetchStudentMetrics = async () => {
    return fetchStudentMetricsForStudents(students);
  };

  const fetchStudentMetricsForStudents = async (studentsData) => {
    try {
      const studentIds = studentsData.map((student) => student.id).filter(Boolean);
      console.log(`Fetching metrics for ${studentIds.length} students:`, studentIds);
      
      // Calculate date ranges
      const currentDate = new Date();

      // Get school year start date from calendaring service
      const { startDate: yearStartDate } = await getSchoolYearDates();

      // For week-to-date, use Saturday 12am UTC to Friday 11:59pm UTC
      const weekStartDate = new Date();
      const dayOfWeek = currentDate.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Calculate days back to Saturday (6)
      // If today is Saturday (6), go back 0 days
      // If today is Sunday (0), go back 1 day  
      // If today is Monday (1), go back 2 days, etc.
      const daysBackToSaturday = dayOfWeek === 6 ? 0 : (dayOfWeek + 1);
      
      weekStartDate.setUTCDate(currentDate.getUTCDate() - daysBackToSaturday);
      weekStartDate.setUTCHours(0, 0, 0, 0); // Saturday 12am UTC

      // Week end is Friday 11:59pm UTC
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setUTCDate(weekStartDate.getUTCDate() + 6); // Add 6 days to get to Friday
      weekEndDate.setUTCHours(23, 59, 59, 999); // Friday 11:59pm UTC

      // Format dates for API calls
      const yearDateStart = yearStartDate.toISOString().split("T")[0];
      const weekDateStart = weekStartDate.toISOString().split("T")[0];
      const weekDateEnd = weekEndDate.toISOString().split("T")[0];
      const dateEnd = currentDate.toISOString().split("T")[0];

      // Make two API calls in parallel - one for the year, one for the week
      const [year, week] = await Promise.all([
        SRS.getStudentMetrics(studentIds, yearDateStart, dateEnd),
        SRS.getStudentMetrics(studentIds, weekDateStart, weekDateEnd),
      ]);

      return studentIds.reduce((acc, studentId, index) => {
        acc[studentId] = {
          year: year[index],
          week: week[index],
        };
        return acc;
      }, {});
    } catch (error) {
      console.error("Error fetching student metrics:", error);
      return {}; // Return empty object instead of rejectWithValue
    }
  };

  return (
    <div className="irip-document-generator">
      {/* Temporarily hidden floating IRIP button */}
      {/*
      <button
        className={`irip-document-generator__button ${
          isGenerating ? "generating" : ""
        }`}
        onClick={generateReportDocuments}
        disabled={isGenerating || !students.length}
      >
        {isGenerating ? (
          <>
            Generating
            <span className="irip-document-generator__progress">
              <span className="irip-document-generator__progress-bar">
                <span
                  className="irip-document-generator__progress-bar-fill"
                  style={{ width: `${progress}%` }}
                />
              </span>
              {progress}%
            </span>
          </>
        ) : (
          <>
            <span className="irip-document-generator__icon">📄</span>
            Generate IRIP Reports
          </>
        )}
      </button>
      */}

      {(progressReport || readingRopeReport) && (
        <div
          style={{
            position: "absolute",
            // This puts it on screen for debugging
            // left: "-1620px",
            // top: "-1080px",
            left: "99999px",
            top: "99999px",
            width: "1250px",
          }}
        >
          {progressReport}
          {readingRopeReport}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { roster } = state.reports;

  return {
    weeklyPlan: get(state, "weeklyPlan.data", []),
    trackingActivities: get(state, "reports.tracking.report.activities", null),
    benchmarkActivities: get(
      state,
      "reports.benchmark.report.activities",
      null
    ),
    progressActivities: get(state, "reports.progress.report.activities", null),
    dyslexiaReport: get(state, "reports.dyslexia.report.data"),
    parentReport: get(state, "reports.parent.report.data"),
    students: roster.data && roster.data.student ? roster.data.student : [],
    // 1 student for testing
    // students:
    //   roster.data && roster.data.student ? [roster.data.student[0]] : [],
    benchmarks: get(state, "reports.teacher.benchmarks", []),
    selectedClass: get(state, "reports.roster.ui.selections.classroom", null),
    selectedEquating: get(state, "reports.teacher.equatingSelection", true),
    selectedMetric: get(state, "reports.teacher.selections.metric", {}),
    selectedScale: get(state, "reports.teacher.selections.scale", {}),
    selectedBenchmark: get(state, "reports.teacher.selections.benchmark", {}),
    selectedStudent: get(state, "reports.roster.ui.selections.student", null),
    selectedSchool: get(state, "reports.roster.ui.selections.school", null),
    selectedDistrict: get(state, "reports.teacher.district", null),
    customBenchmarks: get(state, "reports.teacher.customBenchmarks"),
    selectedLocale: get(state, "reports.teacher.selectedLocale", null),
    selectedAssessmentTypeFilter: get(
      state,
      "reports.teacher.selectedAssessmentTypeFilter",
      null
    ),
    selectedScreeningWindow: get(
      state,
      "reports.teacher.selectedScreeningWindow",
      null
    ),
    selectedStatePercentileCuts: get(
      state,
      "reports.teacher.selectedStatePercentileCuts",
      null
    ),
    selectedCurriculum: get(
      state,
      "reports.teacher.selectedCurriculum.value",
      null
    ),
    reportConfig: get(state, "reports.teacher.reportConfig", null),
    selectedClassProgressReportType: get(
      state,
      "reports.teacher.classProgressReportType",
      null
    ),
  };
};

const mapDispatchToProps = (dispatch) => ({
  // Reports
  fetchProgressReport: (...args) => {
    console.log("WALOO: fetchProgressReport", args);
    dispatch(progressCreators.fetchReport(...args));
  },
  fetchReadingRopeReport: (
    classroomId,
    studentId,
    selectedStatePercentileCuts
  ) => {
    console.log("WALOO: fetchReadingRopeReport", classroomId, studentId);
    dispatch(
      readingRopeCreators.fetchReport(
        classroomId,
        studentId,
        selectedStatePercentileCuts
      )
    );
  },
  clearReport: (reportType) => dispatch(reportCreators.reportClear(reportType)),
  // Teacher state updates
  updateTeacherSelection: (selection, district, students) =>
    dispatch(teacherCreators.teacherSelect(selection, district, students)),
  // Roster state updates
  updateRosterSelection: (selection) =>
    dispatch(rosterCreators.rosterSelect(selection)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(IRIPDocumentGenerator);
